/**
 * ARIA Clinical Vocabulary Engine
 * ---------------------------------------------------------------------------
 * Post-transcription lexical correction. Runs client-side on the raw
 * Web Speech transcript BEFORE it reaches the intent router or the
 * /api/ask backend, so:
 *   • nav commands still match ("tea base" → "Ti-base" doesn't help nav,
 *     but "circonia" → "Zirconia" helps the clinical anchor rule fire)
 *   • the LLM sees the clinician's canonical spelling
 *   • the on-screen "What ARIA heard" transcript reads correctly
 *
 * Pipeline (2026-02 mobile-parity rewrite — founder brief):
 *   The engine is span-based, non-overlapping, longest-match-first, and
 *   idempotent. Given a raw utterance we:
 *     1. Collect every candidate match from every registered rule against
 *        the ORIGINAL transcript (not against a running-mutated buffer).
 *     2. Enforce each rule's context gate against original + extraContext.
 *     3. Rank candidates: longer span > higher confidence > leftmost.
 *     4. Greedy-select non-overlapping candidates. A rule's canonical form
 *        is compiled as a "self-match" that claims its own span so shorter
 *        variants inside the canonical (e.g. "RP" inside "RP platform")
 *        cannot fire — this is what makes correction idempotent: running
 *        the engine twice returns the same corrected string.
 *     5. Apply the accepted rewrites from right to left, so earlier spans'
 *        indices stay valid regardless of length changes.
 *
 *   One source span produces at most ONE accepted correction. The raw
 *   transcript is preserved separately (returned as `original`); the
 *   corrected transcript is returned as `corrected`; the accepted rewrites
 *   are returned as `corrections`.
 *
 * Deterministic, pure. No network calls, no ML, no side effects.
 */

import { PROSTHODONTIC_TERMS } from "./prosthodontics";

/**
 * Deterministic ID derivation. IDs are stable slugs of the canonical /
 * variant string. Frontend + backend derive IDs the same way, so no ID
 * exchange table is required — the vocabulary itself is the contract.
 *
 * Slug rules: lowercase, non-alphanumeric → "_", collapse "_+", strip
 * leading/trailing "_". Examples:
 *   "Ti-base"                       → "ti_base"
 *   "Full-arch prosthesis"          → "full_arch_prosthesis"
 *   "Occlusal vertical dimension (OVD)" → "occlusal_vertical_dimension_ovd"
 */
export function slugify(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}
function _entryId(entry) {
  return entry && entry.canonical ? slugify(entry.canonical) : "";
}
function _variantId(entry, variant) {
  const eid = _entryId(entry);
  if (!eid) return "";
  const v = slugify(variant);
  return v ? `${eid}.v.${v}` : eid;
}

// Specialty registry — populated at module load.
const REGISTRY = new Map();

/**
 * Register a specialty dictionary.
 * @param {string} name    slug: "prosthodontics" | "endodontics" | …
 * @param {Array}  entries [{ canonical, variants, wholeWord? }, …]
 */
export function register(name, entries) {
  if (!name || !Array.isArray(entries)) return;
  REGISTRY.set(name.toLowerCase(), entries);
}

/**
 * Compile a variant string into a case-insensitive regex.
 * By default we match on word boundaries so "trios" doesn't rewrite
 * "patriotism". Set `wholeWord:false` per entry to allow sub-token matches.
 */
function _compileVariant(variant, wholeWord = true) {
  // Escape regex-special chars, then allow flexible whitespace between tokens
  // so "N C M" matches "N   C   M" and "N-C-M".
  const escaped = variant
    .trim()
    .split(/\s+/)
    .map((tok) => tok.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("[\\s\\-\\.]*");
  const boundary = wholeWord ? "\\b" : "";
  return new RegExp(`${boundary}${escaped}${boundary}`, "gi");
}

/**
 * Rate a variant → canonical replacement's confidence.
 *
 *   high    — variant has ≥ 2 tokens OR is ≥ 5 chars AND contains a digit
 *             OR non-alpha character (e.g. "N C M", "Ti-base", "F P 1")
 *   medium  — single-token, alphabetic, ≥ 4 chars ("circonia", "trios")
 *   low     — single-token, ≤ 3 chars OR common everyday English word
 *             ("cr", "mip", "at bottom") — preserved-ambiguity path
 */
const _AMBIGUOUS_ENGLISH = new Set([
  // Kept intentionally empty: multi-token variants get high confidence
  // automatically. Add specific misfires here if we discover any in the
  // field ("cr", "mip" etc. are already handled by the single-token /
  // short-length rule below).
]);
function _confidenceFor(variant) {
  const v = (variant || "").trim();
  if (!v) return "low";
  if (_AMBIGUOUS_ENGLISH.has(v.toLowerCase())) return "low";
  const tokens = v.split(/\s+/);
  if (tokens.length >= 2) return "high";
  if (/[\d\-.·]/.test(v)) return "high";
  if (v.length >= 4) return "medium";
  return "low";
}

const _CONF_ORDER = { low: 0, medium: 1, high: 2 };

/**
 * Compile a whole dictionary once. Cached in a WeakMap keyed by the entries
 * array so repeated calls with the same dictionary are cheap.
 *
 * Each rule includes:
 *   canonical / canonicalId / variantId / variant / confidence / context
 *   pattern      — /g regex for scanning
 *   isCanonical  — true when this rule matches the canonical form itself;
 *                  self-matches CLAIM the span so shorter variants inside
 *                  it (e.g. "RP" inside "RP platform") cannot re-fire.
 *                  Self-matches are never emitted as corrections.
 */
const _compiledCache = new WeakMap();
function _compile(entries) {
  const cached = _compiledCache.get(entries);
  if (cached) return cached;
  // Sort so longer canonical forms are attempted before their shorter
  // substrings (e.g. "Straumann BLX" before "Straumann").
  const sorted = [...entries].sort(
    (a, b) => (b.canonical || "").length - (a.canonical || "").length,
  );
  const rules = [];
  for (const e of sorted) {
    if (!e || !e.canonical || !Array.isArray(e.variants)) continue;
    const canonicalId = _entryId(e);
    const context = Array.isArray(e.context) && e.context.length
      ? e.context.map((c) => String(c).toLowerCase())
      : null;
    const wholeWord = e.wholeWord !== false;
    // Idempotency guard — the canonical form itself is a rule. Its self-
    // match claims the span so shorter variants inside it cannot fire.
    // Confidence is "high" so it ranks above any variant of equal length.
    rules.push({
      canonical: e.canonical,
      canonicalId,
      variantId: canonicalId,
      variant: e.canonical,
      confidence: "high",
      pattern: _compileVariant(e.canonical, wholeWord),
      context,
      isCanonical: true,
    });
    for (const v of e.variants) {
      if (typeof v !== "string" || !v.trim()) continue;
      rules.push({
        canonical: e.canonical,
        canonicalId,
        variantId: _variantId(e, v),
        variant: v,
        confidence: _confidenceFor(v),
        pattern: _compileVariant(v, wholeWord),
        context,
        isCanonical: false,
      });
    }
  }
  _compiledCache.set(entries, rules);
  return rules;
}

/**
 * Enumerate every match of a /g regex against `text` without infinite-loop
 * risk on zero-length matches.
 */
function _allMatches(pattern, text) {
  pattern.lastIndex = 0;
  const out = [];
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = pattern.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    out.push({ start, end, matchText: m[0] });
    if (m[0].length === 0) pattern.lastIndex++;
  }
  return out;
}

/**
 * Apply lexical correction to a raw transcript.
 *
 * @param {string}   text                          raw transcript
 * @param {object}   [opts]
 * @param {string[]} [opts.specialties]            which dictionaries to
 *                                                 apply; default all
 * @param {string}   [opts.minConfidence="medium"] one of "low"|"medium"|"high"
 *                                                 — rewrites below this
 *                                                 confidence are annotated
 *                                                 (see `corrections`) but
 *                                                 the transcript is NOT
 *                                                 silently altered.
 * @param {string}   [opts.extraContext]           prior conversation text
 *                                                 used only to satisfy the
 *                                                 per-rule context gate for
 *                                                 short follow-up utterances
 *                                                 like "RP.".
 * @returns {{ original, corrected, corrections, ambiguous }}
 *   original    — unmodified input
 *   corrected   — transcript with high/medium substitutions applied
 *   corrections — [{ variant, canonical, confidence, applied, start, end }]
 *                 one entry per accepted rewrite; spans are on `original`
 *   ambiguous   — the low-confidence / context-blocked matches that were
 *                 NOT applied
 */
export function correctTranscript(text, opts = {}) {
  const original = text || "";
  if (!original || typeof original !== "string") {
    return {
      original: original || "",
      corrected: original || "",
      corrections: [],
      ambiguous: [],
    };
  }
  const active = (opts.specialties && opts.specialties.length
    ? opts.specialties
    : Array.from(REGISTRY.keys())
  )
    .map((s) => s.toLowerCase())
    .filter((s) => REGISTRY.has(s));
  if (!active.length) {
    return { original, corrected: original, corrections: [], ambiguous: [] };
  }

  const minConf = (opts.minConfidence || "medium").toLowerCase();
  const minRank = _CONF_ORDER[minConf] ?? 1;

  const originalLower = original.toLowerCase();
  const extraContextLower = String(opts.extraContext || "").toLowerCase();
  const contextHaystack = extraContextLower
    ? `${originalLower} ${extraContextLower}`
    : originalLower;

  // ── Step 1 — collect every candidate match against the ORIGINAL text.
  const candidates = [];
  const ambiguous = [];
  for (const specialty of active) {
    const rules = _compile(REGISTRY.get(specialty));
    for (const rule of rules) {
      const hits = _allMatches(rule.pattern, original);
      if (!hits.length) continue;
      // Context gate — if any hit exists but no context token is present,
      // record every hit as ambiguous (context-blocked) once per rule, no
      // matter how many spans it had (they'd all be blocked identically).
      if (rule.context) {
        const hasCtx = rule.context.some((c) => contextHaystack.includes(c));
        if (!hasCtx) {
          if (!rule.isCanonical) {
            ambiguous.push({
              variant: rule.variant,
              canonical: rule.canonical,
              variantId: rule.variantId,
              canonicalId: rule.canonicalId,
              confidence: rule.confidence,
              applied: false,
              skipped: "no_context",
            });
          }
          continue;
        }
      }
      for (const h of hits) {
        candidates.push({
          rule,
          start: h.start,
          end: h.end,
          matchText: h.matchText,
          length: h.end - h.start,
        });
      }
    }
  }

  if (!candidates.length) {
    return { original, corrected: original, corrections: [], ambiguous };
  }

  // ── Step 2 — rank candidates.
  //   1. Longer span wins (longest-match-first).
  //   2. Self-matches (canonical) tie-break above variants of equal span
  //      so an already-canonical phrase claims its region.
  //   3. Higher confidence wins.
  //   4. Leftmost wins.
  candidates.sort((a, b) => {
    if (a.length !== b.length) return b.length - a.length;
    if (a.rule.isCanonical !== b.rule.isCanonical) {
      return a.rule.isCanonical ? -1 : 1;
    }
    const ca = _CONF_ORDER[a.rule.confidence] ?? 1;
    const cb = _CONF_ORDER[b.rule.confidence] ?? 1;
    if (ca !== cb) return cb - ca;
    return a.start - b.start;
  });

  // ── Step 3 — greedy pick non-overlapping candidates. Confidence gate
  //           applied here so below-threshold variants become "ambiguous"
  //           rather than silently rewriting.
  const accepted = [];
  const isFree = (start, end) =>
    !accepted.some((a) => a.start < end && start < a.end);
  for (const c of candidates) {
    if (!isFree(c.start, c.end)) continue;
    if (c.rule.isCanonical) {
      // Claim the span; do not emit a correction.
      accepted.push(c);
      continue;
    }
    const rank = _CONF_ORDER[c.rule.confidence] ?? 1;
    if (rank < minRank) {
      ambiguous.push({
        variant: c.rule.variant,
        canonical: c.rule.canonical,
        variantId: c.rule.variantId,
        canonicalId: c.rule.canonicalId,
        confidence: c.rule.confidence,
        applied: false,
      });
      continue;
    }
    accepted.push(c);
  }

  // ── Step 4 — apply accepted (non-canonical) rewrites right-to-left.
  const rewrites = accepted.filter((a) => !a.rule.isCanonical);
  rewrites.sort((a, b) => b.start - a.start);
  let corrected = original;
  for (const r of rewrites) {
    corrected =
      corrected.slice(0, r.start) + r.rule.canonical + corrected.slice(r.end);
  }
  corrected = corrected.replace(/[ \t]{2,}/g, " ").trim();

  const corrections = rewrites
    .slice()
    .sort((a, b) => a.start - b.start)
    .map((r) => ({
      variant: r.rule.variant,
      canonical: r.rule.canonical,
      variantId: r.rule.variantId,
      canonicalId: r.rule.canonicalId,
      confidence: r.rule.confidence,
      applied: true,
      start: r.start,
      end: r.end,
      sourceText: r.matchText,
    }));

  return { original, corrected, corrections, ambiguous };
}

/** How many terms are recognised across all registered specialties. */
export function vocabularySize() {
  let n = 0;
  for (const entries of REGISTRY.values()) n += entries.length;
  return n;
}

/** How many active specialty dictionaries are loaded. */
export function specialtyCount() {
  return REGISTRY.size;
}

/** For diagnostics / settings surfaces. */
export function listSpecialties() {
  return Array.from(REGISTRY.keys());
}

// ─── Boot: register the initial prosthodontic dictionary ─────────────────
register("prosthodontics", PROSTHODONTIC_TERMS);

/**
 * Lightweight heuristic: does this transcript contain any known
 * clinical vocabulary term? Used by the ARIA router for the clinical
 * intent fallback (§3 in the founder brief).
 *
 * Any registered specialty will trigger — future dictionaries automatically
 * expand the fallback surface without a code change.
 */
export function containsClinicalVocab(text) {
  if (!text || typeof text !== "string") return false;
  for (const entries of REGISTRY.values()) {
    for (const rule of _compile(entries)) {
      rule.pattern.lastIndex = 0;
      if (rule.pattern.test(text)) return true;
    }
  }
  return false;
}
